import { Formik } from 'formik';
import { isEmpty, uniqueId } from 'lodash';
import React from 'react';
import * as Yup from 'yup'; // Add this import
import Form, { FormElement } from '@dashboard/components/Form';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import OptionList from '@dashboard/components/Formik/components/OptionList';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import { formatPriceForInput, formatPriceInput } from '@dashboard/services/formatting/price';
import useProduct from '@/dashboard/scenes/PageEdit/scenes/PageProduct/useProduct';

const ProductDiscountsForm = () => {
  const { product, updateProduct } = useProduct();

  // Add this validation schema
  const validationSchema = Yup.object().shape({
    discounts: Yup.array().of(
      Yup.object().shape({
        quantityCondition: Yup.string().required('Quantity condition is required'),
        variantOptions: Yup.array().when('quantityCondition', {
          is: 'variantOptionQuantity',
          then: Yup.array().min(1, 'Select at least one variant option'),
          otherwise: Yup.array(),
        }),
        minQuantity: Yup.number().required('Minimum quantity is required'),
        maxQuantity: Yup.number().nullable(),
        discountType: Yup.string().required('Discount type is required'),
        amountOff: Yup.string().when('discountType', {
          is: 'amountOff',
          then: Yup.string().required('Discount amount is required'),
          otherwise: Yup.string().nullable(),
        }),
        percentOff: Yup.number().when('discountType', {
          is: 'percentOff',
          then: Yup.number().required('Discount percentage is required'),
          otherwise: Yup.number().nullable(),
        }),
      }),
    ),
  });

  return (
    <Formik
      validationSchema={validationSchema} // Add the validation schema here
      initialValues={{
        discounts:
          product?.discounts?.map(discount => ({
            ...discount,
            key: uniqueId(),
            discountType: discount.amountOff ? 'amountOff' : 'percentOff',
            percentOff: discount.percentOff || '',
            amountOff: discount.amountOff
              ? formatPriceForInput({
                  amount: discount.amountOff,
                  currency: product?.currency,
                  style: 'decimal',
                })
              : '',
            quantityCondition: discount.quantityCondition || 'checkoutQuantity',
            variantOptions: discount.variantOptions || [],
          })) || [],
      }}
      onSubmit={async (values, actions) => {
        actions.setStatus(undefined);

        const body = {
          discounts: values.discounts.map(discount => ({
            minQuantity: discount.minQuantity,
            maxQuantity: discount.maxQuantity,
            percentOff: discount.discountType === 'percentOff' ? discount.percentOff : null,
            amountOff:
              discount.discountType === 'amountOff'
                ? formatPriceInput(discount.amountOff, product?.currency)
                : null,
            quantityCondition: discount.quantityCondition,
            variantOptions:
              discount.quantityCondition === 'variantOptionQuantity' ? discount.variantOptions : [],
          })),
        };

        const { data, error } = await updateProduct.mutateAsync(body);

        if (data) {
          actions.setStatus('');
          actions.resetForm({
            values: {
              discounts: data.discounts.map(discount => ({
                ...discount,
                key: uniqueId(),
                discountType: discount.amountOff ? 'amountOff' : 'percentOff',
                amountOff: discount.amountOff
                  ? formatPriceForInput({
                      amount: discount.amountOff,
                      currency: data.currency,
                      style: 'decimal',
                    })
                  : '',
                percentOff: discount.percentOff || '',
              })),
            },
          });
        } else {
          const { status, errors } = transformApiErrorToFormik(error);

          if (!isEmpty(errors)) {
            actions.setErrors(errors);
          } else {
            actions.setStatus(status);
          }
        }
      }}
    >
      {({ values, status, dirty, handleSubmit, setFieldValue }) => (
        <Form onSubmit={handleSubmit}>
          <FormElement>
            <OptionList
              name="discounts"
              options={values.discounts}
              onChange={options => setFieldValue('discounts', options)}
              optionShape={{
                minQuantity: '',
                maxQuantity: '',
                discountType: 'percentOff',
                amountOff: '',
                percentOff: '',
                quantityCondition: 'checkoutQuantity',
                variantOptions: [],
              }}
              addOptionLabel="+ Add discount rule"
              deleteOptionLabel="Delete discount rule?"
              fields={({ values: fieldValues }) => [
                {
                  name: 'quantityCondition',
                  label: 'Quantity',
                  type: 'radioCard',
                  tooltip:
                    'Apply this discount based on checkout quantity field or quantity per variant option.',
                  options: [
                    { label: 'Checkout quantity', value: 'checkoutQuantity' },
                    { label: 'Per variant option', value: 'variantOptionQuantity' },
                  ],
                  required: true,
                },
                {
                  name: 'variantOptions',
                  label: 'Variant options',
                  type: 'listbox',
                  multiple: true,
                  visible: fieldValues.quantityCondition === 'variantOptionQuantity',
                  placeholder: 'Select variant options',
                  options: product.variants.flatMap(variant =>
                    variant.options.map(option => ({
                      name: `${variant.name}: ${option.name}`,
                      value: option._id,
                    })),
                  ),
                },
                {
                  type: 'columns',
                  columns: 2,
                  gap: 'sm',
                  fields: [
                    {
                      name: 'minQuantity',
                      label: 'Min. quantity',
                      placeholder: 'Min. quantity',
                      type: 'number',
                      required: true,
                    },
                    {
                      name: 'maxQuantity',
                      label: 'Max. quantity',
                      placeholder: 'Max. quantity',
                      type: 'number',
                      optional: true,
                    },
                    {
                      name: 'discountType',
                      label: 'Discount type',
                      type: 'select',
                      options: [
                        { label: 'Fixed amount', value: 'amountOff' },
                        { label: 'Percentage', value: 'percentOff' },
                      ],
                      required: true,
                    },
                    {
                      name: 'amountOff',
                      label: 'Discount amount',
                      placeholder: 'Discount amount',
                      type: 'string',
                      visible: fieldValues.discountType === 'amountOff',
                    },
                    {
                      name: 'percentOff',
                      label: 'Discount %',
                      placeholder: 'Discount %',
                      type: 'number',
                      visible: fieldValues.discountType === 'percentOff',
                    },
                  ],
                },
              ]}
            />
          </FormElement>

          <FormElement>{status && status}</FormElement>

          <FormSubmit>
            <FormSubmit.Button>Save</FormSubmit.Button>
            <FormSubmit.State visible={!!dirty} />
          </FormSubmit>
        </Form>
      )}
    </Formik>
  );
};

export default ProductDiscountsForm;
